import { DateTime } from "luxon"
import { Box, IconButton, Stack, Tooltip } from "@mui/material"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { IconDefinition } from "@fortawesome/fontawesome-svg-core"
import { faFilter } from "@fortawesome/pro-light-svg-icons"
import { makeStyles } from "tss-react/mui"

import { LocalizedStrict } from "../../../../../../localization/components/AppLocalized"
import { useAppLocalization } from "../../../../../../localization/components/AppLocalizationProvider"
import { FilterSearch } from "../../../../../../../shared/components/createOrder/components/FilterSearch"
import { StandardFilterChip } from "../../../../../../../shared/components/filters/StandardFilterChip"

import { NotApprovedToggle } from "./NotApprovedToggle"
import { ProductionDateButton } from "./ProductionDateButton"

export type ProductionFilterChip = Readonly<{
    key: string
    label: string
    icon: IconDefinition
    onRemove: () => void
}>

const useStyles = makeStyles()((theme) => ({
    root: {
        flexWrap: "wrap",
    },
    leftGroup: {
        flexWrap: "wrap",
    },
    chips: {
        flexWrap: "wrap",
    },
    actions: {
        marginLeft: "auto",
    },
    filterButton: {
        width: "3rem",
        height: "3rem",
        background: theme.palette.background.paper,
        border: `1px solid ${theme.palette.tailwind.slate[300]}`,
        borderRadius: ".75rem",
        color: theme.palette.tailwind.slate[800],
        "& svg": {
            fontSize: "1.125rem",
        },
    },
    searchWrap: {
        "& .MuiOutlinedInput-root.MuiOutlinedInput-root": {
            height: "3rem",
            boxShadow: "none",
            paddingLeft: ".875rem",
            paddingRight: ".875rem",
            background: theme.palette.background.paper,
        },
    },
}))

type Props = Readonly<{
    selectedDate: DateTime
    onDateChange: (value: DateTime) => void
    onOpenFilters: () => void
    search: string
    onSearchChange: (value: string) => void
    filterChips: ReadonlyArray<ProductionFilterChip>
    notApprovedView: boolean
    notApprovedCount: number
    onNotApprovedChange: (notApprovedView: boolean) => void
}>

export function ProductionViewToolbar({
    selectedDate,
    onDateChange,
    onOpenFilters,
    search,
    onSearchChange,
    filterChips,
    notApprovedView,
    notApprovedCount,
    onNotApprovedChange,
}: Props) {
    const { classes } = useStyles()
    const { l10n } = useAppLocalization()

    return (
        <Stack direction="row" alignItems="center" spacing="1rem" className={classes.root}>
            <Stack direction="row" alignItems="center" spacing="1.5rem" className={classes.leftGroup}>
                {!notApprovedView && <ProductionDateButton selectedDate={selectedDate} onDateChange={onDateChange} />}

                {filterChips.length > 0 && (
                    <Stack direction="row" spacing=".5rem" className={classes.chips}>
                        {filterChips.map((chip) => (
                            <StandardFilterChip
                                key={chip.key}
                                label={chip.label}
                                icon={chip.icon}
                                onRemove={chip.onRemove}
                            />
                        ))}
                    </Stack>
                )}
            </Stack>

            <Stack direction="row" alignItems="center" spacing=".75rem" className={classes.actions}>
                <NotApprovedToggle isActive={notApprovedView} count={notApprovedCount} onToggle={onNotApprovedChange} />
                <IconButton
                    className={classes.filterButton}
                    onClick={onOpenFilters}
                    aria-label={l10n.getString("meeting-service-production-view-filter-button")}
                >
                    <FontAwesomeIcon icon={faFilter} />
                </IconButton>
                <LocalizedStrict id="meeting-service-production-view-search-input" attrs={{ title: true }}>
                    <Tooltip title="Search for order id, meeting name, location or person.">
                        <Box className={classes.searchWrap}>
                            <FilterSearch
                                search={search}
                                setSearch={onSearchChange}
                                activeWidth="18rem"
                                inactiveWidth="3rem"
                                placeholderId="meeting-service-production-view-search-input"
                            />
                        </Box>
                    </Tooltip>
                </LocalizedStrict>
            </Stack>
        </Stack>
    )
}
